import { LuIcon, Text } from "@overmap-ai/blocks"
import {
	type AssetTypeIdentifier,
	type AssetTypeStatus,
	computeModelIndex,
	type ProcedureStep,
	ProcedureStepFieldValuesReviewType,
	type ProcedureType,
	type Stored,
	UNLABELLED_ASSET_LABEL,
	type User,
} from "@overmap-ai/common"
import { ConditionManager, FieldSchema } from "@overmap-ai/forms"
import {
	type ColumnDef,
	getCoreRowModel,
	getGroupedRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useMemo } from "react"

import { ProcedureStepCompletionStatus } from "../../../enums/procedures"
import { procedureStepCompletionStatusData, procedureStepFieldValuesReviewTypeData } from "../../../mappings/procedures"
import type { ProcedureInitiativeData } from "../../../typings"
import { formatTimestampDate } from "../../../utils/date"
import { getProcedureStepCompletionStatus } from "../../../utils/procedures"
import { AssetTypeIcon } from "../../Icons/AssetTypeIcon"
import { AssetTypeStatusIcon } from "../../Icons/AssetTypeStatusIcon"
import { UserIcon } from "../../Icons/UserIcon"
import { AssetIndexLabel } from "../../IndexLabels/AssetIndexLabel"
import { ColumnSortButton, ColumnSortIcon, RowSelectAllCheckbox, RowSelectCheckbox } from "../components"
import { EmptyColumnCellFallback } from "../components/EmptyColumnCellFallback"
import { getColumnDefsOfFieldSchema, getColumnDefsOfIdentifiers } from "../utils"

interface ProcedureInitiativeDataTableColumnsConfig {
	procedureType: Stored<ProcedureType>
	procedureTypeFieldSchema: FieldSchema
	procedureSteps: Stored<ProcedureStep>[]
	procedureStepFieldSchemasAndConditions: Record<ProcedureStep["uuid"], [FieldSchema, ConditionManager]>
	assetTypeIdentifiers: Stored<AssetTypeIdentifier>[]
	assetTypeFieldSchema: FieldSchema
	assetTypeStatuses: Stored<AssetTypeStatus>[]
}

export const useProcedureInitiativeDataTableColumns = (config: ProcedureInitiativeDataTableColumnsConfig) => {
	const {
		procedureType,
		procedureTypeFieldSchema,
		procedureSteps,
		procedureStepFieldSchemasAndConditions,
		assetTypeIdentifiers,
		assetTypeFieldSchema,
		assetTypeStatuses,
	} = config

	return useMemo(() => {
		const columns: ColumnDef<ProcedureInitiativeData>[] = [
			{
				id: "select",
				enableHiding: false,
				header({ table }) {
					return (
						<RowSelectAllCheckbox
							toggleAllRowsSelected={table.toggleAllRowsSelected}
							isSomeRowsSelected={table.getIsSomeRowsSelected()}
							isAllRowsSelected={table.getIsAllRowsSelected()}
						/>
					)
				},
				cell({ row }) {
					return (
						<RowSelectCheckbox isRowSelected={row.getIsSelected()} toggleRowSelected={row.toggleSelected} />
					)
				},
			},
			{
				id: "index",
				accessorFn: ({ procedure }) => procedure.index ?? Number.MAX_SAFE_INTEGER,
				sortUndefined: "last",
				meta: {
					plainHeader: "Index",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Index
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ row }) {
					return <Text>{computeModelIndex(procedureType, row.original.procedure)}</Text>
				},
			},
			{
				id: "submitted_at",
				sortingFn: "datetime",
				accessorFn: ({ procedure }) => procedure.submitted_at,
				meta: {
					plainHeader: "Submitted at",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Submitted at
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					return <Text accentColor="base">{formatTimestampDate(getValue<string>())}</Text>
				},
			},
			{
				id: "asset",
				accessorFn: ({ asset }) => asset.index,
				meta: {
					plainHeader: "Asset",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Asset
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ row }) {
					const { asset } = row.original

					return (
						<div className="flex items-center gap-2">
							<AssetTypeIcon assetTypeId={asset.asset_type} />
							<AssetIndexLabel assetId={asset.uuid} />
							<Text accentColor="base">{asset.label || UNLABELLED_ASSET_LABEL}</Text>
						</div>
					)
				},
			},
		]

		if (assetTypeIdentifiers.length > 0) {
			columns.push(
				...getColumnDefsOfIdentifiers<Stored<AssetTypeIdentifier>, ProcedureInitiativeData>(
					assetTypeIdentifiers,
					(identifier) => (data) => data.assetTypeIdentifierValues[identifier.uuid]?.value,
				),
			)
		}

		if (assetTypeStatuses.length > 0) {
			columns.push({
				id: "status",
				meta: {
					plainHeader: "Status",
				},
				accessorFn: (data) => data.asset.status,
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ row }) {
					const statusId = row.original.asset.status
					const assetTypeStatus = assetTypeStatuses.find(({ uuid }) => uuid === statusId)

					return (
						<div className="flex items-center gap-2">
							<AssetTypeStatusIcon assetTypeStatusId={statusId} />
							<Text>{assetTypeStatus?.name}</Text>
						</div>
					)
				},
			})
		}

		if (assetTypeFieldSchema.getFields().length > 0) {
			columns.push(
				...getColumnDefsOfFieldSchema<ProcedureInitiativeData>(assetTypeFieldSchema, (data) =>
					assetTypeFieldSchema.deserializeValues(data.assetTypeFieldValues?.values ?? {}),
				),
			)
		}

		columns.push(
			...getColumnDefsOfFieldSchema<ProcedureInitiativeData>(procedureTypeFieldSchema, (data) =>
				procedureTypeFieldSchema.deserializeValues(data.procedureFieldValues?.values ?? {}),
			),
		)

		for (const procedureStep of procedureSteps) {
			const [fieldSchema, conditionManager] = procedureStepFieldSchemasAndConditions[procedureStep.uuid]

			columns.push({
				id: procedureStep.uuid,
				enableHiding: true,
				meta: {
					plainHeader: procedureStep.name,
				},
				header({ column }) {
					return <Text accentColor="base">{column.columnDef.meta?.plainHeader}</Text>
				},
				columns: [
					{
						id: `${procedureStep.uuid}:completion_status`,
						enableHiding: true,
						accessorFn: (data) =>
							getProcedureStepCompletionStatus(
								fieldSchema,
								conditionManager,
								data.procedureStepFieldValues[procedureStep.uuid]?.values,
								!data.procedureStepFieldValues[procedureStep.uuid]?.published_at,
							),
						meta: {
							plainHeader: `Completion`,
						},
						header({ column }) {
							return (
								<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
									{column.columnDef.meta?.plainHeader}
									<ColumnSortIcon sortDirection={column.getIsSorted()} />
								</ColumnSortButton>
							)
						},
						cell({ getValue }) {
							const status = getValue<ProcedureStepCompletionStatus>()
							const { color, icon, label } = procedureStepCompletionStatusData[status]

							return (
								<Text className="inline-flex items-center gap-1">
									<LuIcon icon={icon} color={color} />
									{label}
								</Text>
							)
						},
					},
					{
						id: `${procedureStep.uuid}:review_status`,
						accessorFn: (data) => data.procedureStepFieldValuesReviews[procedureStep.uuid]?.type,
						sortUndefined: "last",
						enableHiding: true,
						meta: {
							plainHeader: `Review`,
						},
						header({ column }) {
							return (
								<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
									{column.columnDef.meta?.plainHeader}
									<ColumnSortIcon sortDirection={column.getIsSorted()} />
								</ColumnSortButton>
							)
						},
						cell({ getValue }) {
							const reviewType = getValue<ProcedureStepFieldValuesReviewType | undefined>()

							if (reviewType === undefined) return <EmptyColumnCellFallback />

							const { color, icon, statusLabel } = procedureStepFieldValuesReviewTypeData[reviewType]

							return (
								<Text className="inline-flex items-center gap-1">
									<LuIcon icon={icon} color={color} />
									{statusLabel}
								</Text>
							)
						},
					},
					{
						id: `${procedureStep.uuid}:assignees`,
						enableHiding: true,
						accessorFn: (data) =>
							(data.procedureStepAssignees[procedureStep.uuid] ?? []).map(
								(procedureStepAssignee) => procedureStepAssignee.user,
							),
						meta: {
							plainHeader: "Assignees",
						},
						header({ column }) {
							return (
								<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
									{column.columnDef.meta?.plainHeader}
									<ColumnSortIcon sortDirection={column.getIsSorted()} />
								</ColumnSortButton>
							)
						},
						cell({ getValue }) {
							const userIds = getValue<User["id"][]>()

							if (userIds.length === 0) return <EmptyColumnCellFallback />

							return (
								<div className="flex items-center gap-1">
									{userIds.map((userId) => (
										<UserIcon key={userId} userId={userId} size="xs" />
									))}
								</div>
							)
						},
					},
					{
						id: `${procedureStep.uuid}:reviewers`,
						enableHiding: true,
						accessorFn: (data) =>
							(data.procedureStepReviewers[procedureStep.uuid] ?? []).map(
								(procedureStepReviewer) => procedureStepReviewer.user,
							),
						meta: {
							plainHeader: "Reviewers",
						},
						header({ column }) {
							return (
								<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
									{column.columnDef.meta?.plainHeader}
									<ColumnSortIcon sortDirection={column.getIsSorted()} />
								</ColumnSortButton>
							)
						},
						cell({ getValue }) {
							const userIds = getValue<User["id"][]>()

							if (userIds.length === 0) return <EmptyColumnCellFallback />

							return (
								<div className="flex items-center gap-1">
									{userIds.map((userId) => (
										<UserIcon key={userId} userId={userId} size="xs" />
									))}
								</div>
							)
						},
					},
					...getColumnDefsOfFieldSchema<ProcedureInitiativeData>(fieldSchema, (data) =>
						fieldSchema.deserializeValues(data.procedureStepFieldValues[procedureStep.uuid]?.values ?? {}),
					),
				],
			})
		}

		return columns
	}, [
		assetTypeFieldSchema,
		assetTypeIdentifiers,
		assetTypeStatuses,
		procedureStepFieldSchemasAndConditions,
		procedureSteps,
		procedureType,
		procedureTypeFieldSchema,
	])
}

export const useProcedureInitiativeDataTable = (
	args: Omit<TableOptions<ProcedureInitiativeData>, "getCoreRowModel">,
) => {
	return useReactTable<ProcedureInitiativeData>({
		...args,
		getRowId: ({ procedure }) => procedure.uuid,
		getCoreRowModel: getCoreRowModel(),
		getGroupedRowModel: getGroupedRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
	})
}
